import { PaginationReturn } from '@common/types/service';
import { CloseButton } from '@components/utility/buttons';
import { EventListCard } from '@event/components/display/cards/EventListCard';
import { usePublicEventContext } from '@event/contexts/PublicEventContext';
import { useGetUserRequiredSubscribedEventsQuery } from '@event/services/events';
import { EventTypes, RegistrationEventListItem } from '@event/types/event';
import { useAuth } from '@hooks/useAuth';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useGetMarketsQuery } from '@services/markets';
import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

const MIN_ITEMS_THRESHOLD = 4;

type TemporaryRequiredEventsDialogProps = Omit<DialogProps, 'open'> & {
    state: false | 'view' | 'edit';
    handleRegister: () => Promise<void>;
};

const TemporaryRequiredEventsDialog = (props: TemporaryRequiredEventsDialogProps) => {
    const { data: markets } = useGetMarketsQuery(undefined, {
        skip: !props.state,
    });
    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const { user } = useAuth();
    const [page, setPage] = useState(1);

    const {
        register: { loading: isRegistering },
    } = usePublicEventContext();

    const {
        data: eventsResponse,
        isFetching,
        isLoading,
        isSuccess,
    } = useGetUserRequiredSubscribedEventsQuery(
        {
            id: Number(eventId),
            type: eventType,
            page_size: 10,
            page,
        },
        {
            skip: !props.state,
        }
    );

    const events = useMemo(() => eventsResponse as PaginationReturn<RegistrationEventListItem>, [eventsResponse]);

    const scrollRef = useRef<HTMLElement>(null);
    const { items } = useInfiniteScroll({
        scrollElement: scrollRef.current,
        data: {
            results: events?.results ?? [],
            count: Number(events?.count),
            isFetching,
        },
        shouldPopulate: isSuccess && !!events.results.length,
        pageState: [page, setPage],
    });

    const showSpinner = isLoading || (isFetching && events?.results.length > MIN_ITEMS_THRESHOLD);

    const handleConfirm = async () => {
        await props.handleRegister();
        props.onClose?.({}, 'backdropClick');
    };

    return (
        <Dialog {...props} open={!!props.state} classes={{ paper: 'dialog-paper px-[10px] sm:px-[27px] pt-10' }}>
            <CloseButton onClick={() => props.onClose?.({}, 'backdropClick')} />
            <DialogTitle align="center">
                <Typography fontFamily="museo-sans" fontSize={32} letterSpacing={-0.8}>
                    <FormattedMessage id="event.temporary.title" defaultMessage="Temporary Registration" />
                </Typography>
                <Typography fontSize={16} letterSpacing={0} mt={1} color="neutral-900.main">
                    <FormattedMessage
                        id="event.temporary.description"
                        defaultMessage="Please notice that your registration for this event is temporary. To confirm your attendance, you must first attend one of the following events you're already registered for:"
                    />
                </Typography>
            </DialogTitle>
            <DialogContent ref={scrollRef} className="p-0">
                {items.length > 0 ? (
                    <div className="flex flex-col gap-4 h-full relative sm:gap-2.5 p-1">
                        {items.map((event) => (
                            <EventListCard
                                key={`required-event-${event.type}-${event.id}`}
                                event={event}
                                markets={markets}
                                user={user}
                                target="_blank"
                            />
                        ))}
                    </div>
                ) : !showSpinner ? (
                    <div className="m-auto w-fit my-2">
                        <p className="bg-charleston-green-10 p-4 rounded font-medium text-base text-dark-jungle-green">
                            <FormattedMessage id="event.noEvents" defaultMessage="No events found" />
                        </p>
                    </div>
                ) : null}
                {showSpinner ? (
                    <div className="flex items-center justify-center min-h-[100px]">
                        <CircularProgress />
                    </div>
                ) : null}
            </DialogContent>
            {props.state === 'edit' && (
                <DialogActions>
                    <Button
                        className="rounded-full black-border"
                        fullWidth
                        onClick={handleConfirm}
                        disabled={isRegistering}
                        startIcon={isRegistering ? <CircularProgress size={20} /> : null}
                    >
                        <FormattedMessage
                            id="event.temporary.confirm"
                            defaultMessage="Confirm Temporary Registration"
                        />
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export { TemporaryRequiredEventsDialog };
